import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { ReactElement, SyntheticEvent, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { imagesClient } from '../../api/client';
import { ImageRequestUpdateItem } from '../../api/schema';
import { checkProtectedImage, getImagesByOwnerId, listImagesByOwnerId } from '../../redux/slices/images/imagesSlice';
import { createManufacturer, getManufacturerById, updateManufacturer } from '../../redux/slices/manufacturers/manufacturersSlice';
import { getUserData } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { GetValidationErrors } from '../../tool/validation';
import { UploadImage } from '../uploadImage/UploadImage';

interface IProp {
  manufacturerID?: string;
  onClose: (redirectURL?: string) => void;
  open: boolean;
}

interface Values {
  id: string;
  organization_id: string;
  name: string;
  active: boolean;
}

const schema = yup.object().shape({
  name: yup.string().min(2, 'Must be at least 2 characters').required('Required field'),
});

export const ManufacturerForm = ({ onClose, manufacturerID, open }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector(getUserData);
  const manufacturer = useSelector(getManufacturerById(manufacturerID));
  const imageKeys = useSelector(getImagesByOwnerId(manufacturerID));
  const protectedImage = useSelector(checkProtectedImage(manufacturerID));
  const [image, setImage] = useState<ImageRequestUpdateItem | undefined>();

  const onUpload = (data: string, mimeType: string) => {
    setImage({ image_data: data, mime_type: mimeType });
  };

  const submit = async (values: Values, { setSubmitting, setErrors }: FormikHelpers<Values>) => {
    try {
      let manufacturerId = manufacturer?.id;
      setSubmitting(true);
      if (manufacturerId) {
        await dispatch(updateManufacturer(values)).unwrap();
      } else {
        const response = await dispatch(createManufacturer(values)).unwrap();
        manufacturerId = response.data.result?.id;
      }

      if (image?.image_data !== undefined && manufacturerId) {
        await imagesClient.deleteImageByOwnerId(manufacturerId);
        if (image?.image_data) {
          image.id = manufacturerId;
          await imagesClient.createImage(image);
        }
        await dispatch(listImagesByOwnerId(manufacturerId));
      }

      onClose(`/admin/products/manufacturers/${manufacturerId}/details`);
      // eslint-disable-next-line
    } catch (e: any) {
      setErrors(GetValidationErrors(e.response.status, navigate, e.response.data.messages));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  const disabled = !(!manufacturerID || (manufacturerID && manufacturer?.id));

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      <Formik
        validationSchema={schema}
        onSubmit={submit}
        initialValues={{
          id: manufacturer?.id || '',
          organization_id: userData?.organization_id || '',
          name: manufacturer?.name || '',
          active: manufacturer?.active !== undefined ? manufacturer?.active : true,
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          // isValid,
          errors,
          isSubmitting,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>{manufacturer?.id ? 'Edit' : 'Create'} Manufacturer</DialogTitle>
            <DialogContent>
              <Grid container spacing={6}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Name"
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    my={2}
                    required
                    disabled={disabled}
                  />
                  {manufacturer?.id && (
                    <FormControlLabel
                      control={<Switch checked={values.active} onChange={handleChange} name="active" onBlur={handleBlur} />}
                      label="Active"
                      disabled={disabled}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <UploadImage
                    width="100%"
                    imageKey={imageKeys && imageKeys.length > 0 ? imageKeys[0].toLowerCase() : undefined}
                    onUpload={onUpload}
                    disabled={disabled}
                    protectedImage={protectedImage}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 6, pb: 6 }}>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={disabled || isSubmitting}>
                {manufacturer?.id ? 'Save' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
